/**
 * ControlCardTemplateBase
 * 
 * Abstract base class for control card templates.
 * Implements template method pattern for consistent PDF generation.
 */

import { jsPDF } from 'jspdf'
import { CONTROL_CARD_CONFIG } from '../../config'
import type { ControlCardData } from '../../infrastructure'
import { PDFRenderer, ImageRenderer } from '../../infrastructure'

/**
 * Logo configuration
 */
export interface LogoConfig {
  company?: string
  certification?: string
}

/**
 * Render options
 */
export interface RenderOptions {
  finalize?: boolean
  inspectionId?: string
  attachmentImage?: string // Base64 encoded image for second page
}

/**
 * Abstract base template for control cards
 */
export abstract class ControlCardTemplateBase {
  protected readonly config: typeof CONTROL_CARD_CONFIG
  protected pdfRenderer: PDFRenderer
  protected imageRenderer: ImageRenderer

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    this.config = config
    this.pdfRenderer = new PDFRenderer(config)
    this.imageRenderer = new ImageRenderer(config)
  }

  /**
   * Main template method - defines the rendering flow
   */
  async render(
    data: ControlCardData,
    logos?: LogoConfig,
    options?: RenderOptions
  ): Promise<jsPDF> {

    // Reset PDF renderer
    this.pdfRenderer = new PDFRenderer(this.config)

    // Render sections in order
    let currentY = await this.renderHeader(data, logos)

    currentY = await this.renderBody(data, currentY, logos)

    // Watermark on body pages if not finalized
    if (!options?.finalize) {
      this.imageRenderer.addWatermark(this.pdfRenderer.getDocument())
    }

    // Append attachment image as an additional page BEFORE rendering footers
    // so footer/pagination iterates over it as well.
    if (options?.attachmentImage) {
      await this.addImagePage(options.attachmentImage, options?.finalize)
    }

    // Footer/pagination – iterates every page (including the image page)
    await this.renderFooter(data)

    return this.pdfRenderer.getDocument()
  }

  /**
   * Render header section (must be implemented by subclasses)
   */
  protected abstract renderHeader(
    data: ControlCardData,
    logos?: LogoConfig
  ): Promise<number>

  /**
   * Render body section (must be implemented by subclasses)
   */
  protected abstract renderBody(
    data: ControlCardData,
    startY: number,
    logos?: LogoConfig
  ): Promise<number>

  /**
   * Render footer section (must be implemented by subclasses)
   */
  protected abstract renderFooter(data: ControlCardData): Promise<void>

  /**
   * Get the rendered PDF as blob
   */
  getBlob(): Blob {
    return this.pdfRenderer.getBlob()
  }

  /**
   * Save PDF to file
   */
  save(filename: string): void {
    this.pdfRenderer.save(filename)
  }

  /**
   * Get PDF as data URL
   */
  getDataUrl(): string {
    return this.pdfRenderer.getDataUrl()
  }

  /**
   * Get PDF as array buffer
   */
  getArrayBuffer(): ArrayBuffer {
    return this.pdfRenderer.getArrayBuffer()
  }

  /**
   * Add an image as an additional page.
   *
   * The frame matches the control card's visual style:
   *   - black, hairline (0.1 mm) outer border
   *   - same horizontal/top margins as the card body
   *   - reserved bottom margin so the shared footer + pagination
   *     (drawn afterwards by `renderFooter`) sit below the rectangle
   */
  private async addImagePage(imageDataUrl: string, finalize?: boolean): Promise<void> {
    const doc = this.pdfRenderer.getDocument()

    doc.addPage()

    // A4 + layout constants matching the prototype card layout
    const pageWidth = 210
    const pageHeight = 297
    const M_LR = 12
    const M_TOP = 8
    const M_BOTTOM_RESERVED = 14 // leave room for footer text + page numbers
    const BORDER_W = 0.1

    const frameX = M_LR
    const frameY = M_TOP
    const frameW = pageWidth - 2 * M_LR
    const frameH = pageHeight - M_TOP - M_BOTTOM_RESERVED

    // Outer border (black, thin – matches control card tables)
    doc.setDrawColor(0, 0, 0)
    doc.setLineWidth(BORDER_W)
    doc.rect(frameX, frameY, frameW, frameH)

    // Inner image area with a small padding inside the frame
    const innerPad = 5
    const contentX = frameX + innerPad
    const contentY = frameY + innerPad
    const contentWidth = frameW - 2 * innerPad
    const contentHeight = frameH - 2 * innerPad
    
    try {
      // Get image dimensions
      const img = new Image()
      img.src = imageDataUrl
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          const imgWidth = img.width
          const imgHeight = img.height
          const imgRatio = imgWidth / imgHeight
          
          // Calculate scaled dimensions to fit within content area
          let scaledWidth = contentWidth
          let scaledHeight = contentWidth / imgRatio
          
          if (scaledHeight > contentHeight) {
            scaledHeight = contentHeight
            scaledWidth = contentHeight * imgRatio
          }
          
          // Center the image in the content area
          const imageX = contentX + (contentWidth - scaledWidth) / 2
          const imageY = contentY + (contentHeight - scaledHeight) / 2
          
          // Add image to PDF
          doc.addImage(
            imageDataUrl,
            'JPEG',
            imageX,
            imageY,
            scaledWidth,
            scaledHeight
          )
          
          resolve()
        }
        
        img.onerror = () => {
          reject(new Error('Failed to load image'))
        }
      })
      
      // Add watermark on second page if not finalized
      if (!finalize) {
        this.imageRenderer.addWatermark(doc)
      }
    } catch (error) {
      console.error('Error adding image page:', error)
      // Add error message on the page instead
      doc.setFontSize(12)
      doc.setTextColor(255, 0, 0) // Red color
      doc.text('Feil ved lasting av bilde', pageWidth / 2, pageHeight / 2, { align: 'center' })
    }
  }
}

